import React, {
  useContext,
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useForm, useWatch, useFormState, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import Tooltip from 'components/Tooltip';
import LoaderBar from 'components/LoaderBar';
import ErrorMessage from 'components/ErrorMessage';
import LabelWithChildren from 'components/LabelWithChildren';
import DashboardContainer from 'components/DashboardContainer';
import Button from 'components/Button';
import SelectInput from 'components/SelectInput';
import FormInput from 'components/FormInput';
import PhoneInput from 'components/PhoneInput';
import CountrySelect from 'components/CountrySelect';
import HopRedirection from './HopRedirection';

import { newCompany as TEXT } from 'locales/en';
import { BUSINESS_TYPES } from 'utils/selectOptions';
import { logEvent } from 'utils/ga';
import { BrandContext } from 'utils/brand-context';

import { getMerchantCategoryCode, getUserClients } from 'redux/slices/company';
import { createCompanyAndStore, generateHopUrl } from 'redux/slices/company';
import schema, { FIELD_NAMES, defaultValues } from './NewCompany.schema';

const LABEL_CLASS_NAME =
  'pb-1 lg:pb-0 lg:mr-4 min-w-44 lg:w-4/12 lg:flex-shrink-0 lg:flex-grow-0';

const NewCompany = () => {
  const brand = useContext(BrandContext);
  const dispatch = useDispatch();
  const nodeRef = useRef(null);
  const [mccCodeSelected, setMccCodeSelected] = useState(false);
  const {
    merchant_category_codes: merchantCategoryCodes = [],
    hopRedirecting,
    hop_error = {},
  } = useSelector((state) => state.company);
  const user = useSelector((state) => state.auth.user);
  const { register, handleSubmit, control, clearErrors, setValue } = useForm({
    resolver: yupResolver(schema),
    defaultValues,
  });

  useEffect(() => {
    dispatch(getMerchantCategoryCode());
    dispatch(getUserClients());
  }, [dispatch]);

  const legal_entity = useWatch({
    control,
    name: FIELD_NAMES.legal_entity,
  });
  const { errors, isSubmitting } = useFormState({ control });

  const timer = useRef();

  useEffect(() => {
    document.title = `Welcome to ${brand.name} | Javelin`;
  }, [brand.name]);

  const _generateHopUrl = useCallback(async () => {
    const { payload } = await dispatch(
      generateHopUrl({ displayRedirection: true })
    );
    if (!!payload.url) {
      window.location.replace(payload.url);
    }
  }, [dispatch]);

  useEffect(() => {
    if (
      hop_error.status === 400 &&
      hop_error.message?.indexOf('Failed to retrieve account holder') > -1
    ) {
      timer.current = setInterval(() => {
        _generateHopUrl();
      }, 5000);
    } else {
      if (timer.current) clearInterval(timer.current);
    }
    return () => clearInterval(timer.current);
  }, [_generateHopUrl, hop_error]);

  const onSubmit = (data) => {
    const {
      legal_business_name,
      first_name,
      last_name,
      email,
      phone_country,
      phone_number,
      store_name,
      mcc_code,
    } = data;

    return dispatch(
      createCompanyAndStore({
        first_name,
        last_name,
        email,
        phone_number: `${phone_country.dial_code}${phone_number}`,
        legal_entity: legal_entity.value,
        legal_business_name,
        mcc_code: mcc_code.identifier,
        country_code: data[FIELD_NAMES.country].code,
        country_name: data[FIELD_NAMES.country].name,
        name: store_name,
      })
    ).then((action) => {
      if (action.type.endsWith('fulfilled')) {
        _generateHopUrl();
      }
    });
  };

  const onChangeBusinessType = (option, _cb) => {
    if (option.value !== legal_entity?.value) {
      _cb(option);
      setTimeout(() => {
        clearErrors();
      }, 50);
      if (!mccCodeSelected) {
        const defaultMccCode =
          option.value === TEXT.types.individual
            ? merchantCategoryCodes.find((item) => item.identifier === '8398')
            : undefined;
        setValue(FIELD_NAMES.mcc_code, defaultMccCode);
      }
    }
  };

  const renderBusinessName = useMemo(() => {
    if (legal_entity === null) return null;
    return (
      legal_entity.value !== TEXT.types.individual && (
        <FormInput
          label={TEXT.legal_business_name}
          name={FIELD_NAMES.legal_business_name}
          register={register}
          labelClassName={LABEL_CLASS_NAME}
          error={errors.legal_business_name?.message}
        />
      )
    );
  }, [errors.legal_business_name, legal_entity, register]);

  const renderUserName = useMemo(() => {
    if (legal_entity === null) return null;
    return (
      legal_entity.value === TEXT.types.individual && (
        <LabelWithChildren
          label={TEXT.authorized_payee}
          labelClassName={LABEL_CLASS_NAME}
          name={FIELD_NAMES.first_name}
        >
          <div className="relative grid grid-cols-2 justify-start gap-2">
            <div>
              <input
                {...register(FIELD_NAMES.first_name, {
                  required: true,
                })}
                autoComplete="new-password"
                id={FIELD_NAMES.first_name}
                type="text"
                className={`input input-full-border mr-4 ${
                  !!errors.first_name?.message ? 'error' : ''
                }`}
                placeholder={TEXT.first_name}
              />
              {!!errors.first_name?.message && (
                <ErrorMessage message={errors.first_name.message} />
              )}
            </div>
            <div>
              <input
                {...register(FIELD_NAMES.last_name, { required: true })}
                autoComplete="new-password"
                id={FIELD_NAMES.last_name}
                type="text"
                className={`input input-full-border ${
                  !!errors.last_name?.message ? 'error' : ''
                }`}
                placeholder={TEXT.last_name}
              />
              {!!errors.last_name?.message && (
                <ErrorMessage message={errors.last_name.message} />
              )}
            </div>
          </div>
        </LabelWithChildren>
      )
    );
  }, [errors.first_name, errors.last_name, legal_entity, register]);

  if (!merchantCategoryCodes.length || !user.email)
    return <LoaderBar isVisible />;

  return (
    <DashboardContainer
      header={
        hopRedirecting ? (
          TEXT.readyHeader1
        ) : (
          <>
            {TEXT.header1(brand.name)}
            <div className="text-sm font-normal text-javelin-gray-light lg:text-base">
              {TEXT.poweredBy}
            </div>
          </>
        )
      }
      subheader={hopRedirecting ? '' : TEXT.header2}
    >
      {hopRedirecting ? (
        <HopRedirection brand={brand} />
      ) : (
        <form
          noValidate
          onSubmit={handleSubmit(onSubmit)}
          ref={nodeRef}
          className="w-full"
        >
          <div className="card h-full min-h-60 w-full overflow-visible p-5 shadow-sm lg:p-12">
            <h3 className="text-xl font-bold text-javelin-gray-dark">
              {TEXT.formHeader1}
            </h3>
            <h4 className="box-content pt-4 text-left text-sm text-javelin-gray-light lg:text-base">
              {TEXT.formSubheader1}
            </h4>
            <div
              data-testid="public"
              className="settings-card transition-all lg:w-4/5 xl:w-3/5"
            >
              <Controller
                control={control}
                name={FIELD_NAMES.legal_entity}
                render={({ field: { ref, value, onChange, ..._field } }) => (
                  <SelectInput
                    {..._field}
                    value={value}
                    testId={FIELD_NAMES.legal_entity}
                    name={FIELD_NAMES.legal_entity}
                    label={
                      <div className="flex flex-row items-start">
                        <span>{TEXT.legal_entity}</span>
                        <Tooltip text={TEXT.type_tooltip} />
                      </div>
                    }
                    labelClassName={LABEL_CLASS_NAME}
                    options={BUSINESS_TYPES}
                    inputBelowLabel={false}
                    isSearchable={false}
                    placeholder={TEXT.legal_entity_placeholder}
                    error={errors[FIELD_NAMES.legal_entity]?.message}
                    onChange={(option) => {
                      logEvent('select_option', {
                        category: 'account_type',
                        label: option.value,
                      });
                      onChangeBusinessType(option, onChange);
                    }}
                  />
                )}
              />
              {renderBusinessName}
              {renderUserName}
              {!!legal_entity && (
                <>
                  <FormInput
                    label={
                      <div className="flex flex-row items-start">
                        <span className="lg:whitespace-pre-wrap">
                          {TEXT.store_name}
                        </span>
                        <Tooltip text={TEXT.store_name_tooltip} />
                      </div>
                    }
                    labelClassName={LABEL_CLASS_NAME}
                    name={FIELD_NAMES.store_name}
                    register={register}
                    required
                    error={errors[FIELD_NAMES.store_name]?.message}
                  />
                  <Controller
                    control={control}
                    name={FIELD_NAMES.mcc_code}
                    render={({ field: { ref, value, ..._field } }) => (
                      <SelectInput
                        {..._field}
                        value={value || null}
                        testId={FIELD_NAMES.mcc_code}
                        name={FIELD_NAMES.mcc_code}
                        label={TEXT.industry}
                        options={merchantCategoryCodes}
                        inputBelowLabel={false}
                        getOptionLabel={(option) => option.description}
                        getOptionValue={(option) => option.identifier}
                        onChange={(e) => {
                          if (!mccCodeSelected) {
                            setMccCodeSelected(true);
                          }
                          _field.onChange(e);
                        }}
                        error={
                          errors[FIELD_NAMES.mcc_code]?.identifier?.message
                        }
                        placeholder={TEXT.industry_placeholder}
                        labelClassName={LABEL_CLASS_NAME}
                      />
                    )}
                  />
                  <CountrySelect
                    name={FIELD_NAMES.country}
                    label={TEXT.country}
                    control={control}
                    required
                    error={errors[FIELD_NAMES.country]?.name?.message}
                    inputBelowLabel={false}
                    isDisabled
                    labelClassName={LABEL_CLASS_NAME}
                  />
                </>
              )}
            </div>
          </div>
          <div className="card mt-5 overflow-visible p-5 shadow-sm lg:mt-7 lg:p-12">
            <h3 className="text-xl font-bold text-javelin-gray-dark">
              {TEXT.formHeader2}
            </h3>
            <h4 className="box-content pt-4 text-left text-sm text-javelin-gray-light lg:text-base">
              {TEXT.formSubheader2}
            </h4>
            <div
              data-testid="contact-info"
              className="settings-card lg:w-4/5 xl:w-3/5"
            >
              <LabelWithChildren
                label={TEXT.contact_name}
                name={FIELD_NAMES.contact_first_name}
                labelClassName={LABEL_CLASS_NAME}
              >
                <div className="relative grid grid-cols-2 justify-start gap-2">
                  <div>
                    <Controller
                      control={control}
                      name={FIELD_NAMES.contact_first_name}
                      defaultValue={user.firstName}
                      required
                      render={({ field }) => {
                        return (
                          <input
                            {...field}
                            type="text"
                            className={`input input-full-border mr-4 ${
                              !!errors[FIELD_NAMES.contact_first_name]?.message
                                ? 'error'
                                : ''
                            }`}
                            placeholder={TEXT.first_name}
                          />
                        );
                      }}
                    />

                    {!!errors[FIELD_NAMES.contact_first_name]?.message && (
                      <ErrorMessage
                        message={
                          errors[FIELD_NAMES.contact_first_name]?.message
                        }
                      />
                    )}
                  </div>
                  <div>
                    <Controller
                      control={control}
                      name={FIELD_NAMES.contact_last_name}
                      defaultValue={user.lastName}
                      required
                      render={({ field }) => {
                        return (
                          <input
                            {...field}
                            type="text"
                            className={`input input-full-border ${
                              !!errors[FIELD_NAMES.contact_last_name]?.message
                                ? 'error'
                                : ''
                            }`}
                            placeholder={TEXT.last_name}
                          />
                        );
                      }}
                    />
                    {!!errors[FIELD_NAMES.contact_last_name]?.message && (
                      <ErrorMessage
                        message={errors[FIELD_NAMES.contact_last_name]?.message}
                      />
                    )}
                  </div>
                </div>
              </LabelWithChildren>
              <PhoneInput
                name={FIELD_NAMES.phone_number}
                control={control}
                countryFieldName={FIELD_NAMES.phone_country}
                label={TEXT.phone_number}
                error={errors[FIELD_NAMES.phone_number]?.message}
                defaultCountry={user.phone_country}
                defaultPhoneNumber={user.phone}
                inputBelowLabel={false}
                className="flex-1"
                labelClassName={LABEL_CLASS_NAME}
              />
              <FormInput
                label={TEXT.email}
                name={FIELD_NAMES.email}
                type="email"
                register={register}
                error={errors[FIELD_NAMES.email]?.message}
                defaultValue={user.email}
                labelClassName={LABEL_CLASS_NAME}
              />
            </div>
          </div>
          <Button
            className="btn-primary ml-auto mt-4 block p-4 py-3 lg:w-64"
            type="submit"
            title={TEXT.submit}
            showSpinner={isSubmitting}
            isDisabled={isSubmitting}
          />
        </form>
      )}
    </DashboardContainer>
  );
};

export default NewCompany;
